/**
 * Module-level "is there a live call up right now" flag — beads F1.
 *
 * Exists so a session-ending event (a 60-minute agent JWT expiring, the
 * routine case) can be answered correctly WITHOUT inventing a second,
 * parallel notion of "busy": the only source of truth for whether a call is
 * genuinely live is `Softphone.hasActiveSession()` (see its own doc
 * comment), and AgentConsole is the one component that already knows that
 * answer on every real state transition. This module is just the
 * synchronous, cross-tree channel that answer travels through to
 * AuthContext, which runs entirely outside AgentConsole's own render tree
 * and cannot call a method on a ref it doesn't hold.
 *
 * A plain module-scoped variable + listener set, not React context: the
 * consumer that matters most (AuthContext's session-ended handler) needs to
 * read the CURRENT value synchronously the instant a 401 fires — not on some
 * future render once context propagates — and needs to be notified the
 * instant it flips back to false so a deferred sign-out can finally apply.
 * AgentConsole is the only writer; every other consumer only ever reads or
 * subscribes.
 */

type Listener = () => void;

let active = false;
const listeners = new Set<Listener>();

/** Written only by AgentConsole, from its own onState-driven effect. */
export function setCallGuardActive(next: boolean): void {
  if (next === active) return;
  active = next;
  listeners.forEach((listener) => listener());
}

/** Read synchronously by anything that needs "is a call live right now"
 * without waiting for a render (e.g. AuthContext's session-ended handler). */
export function isCallGuardActive(): boolean {
  return active;
}

/** Notified every time the flag actually changes value. Returns an
 * unsubscribe function. */
export function subscribeCallGuard(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
